import { z } from 'zod';
import { fetchPost } from 'src_common/common/realtime-server/fetch';
import { decodePostPlaceBetResponse, BettingPlaceBetType, NewTradingPostBetData } from './postPlaceBetTypes';
import { ChannelType, RabPostBodyItemType } from './types';

const Response422ZOD = z.object({
    code: z.string(),
    debugDetails: z.string().nullable().optional(),
    details: z.unknown(),
    errors: z
        .array(
            z.object({
                code: z.string(),
                debugDetails: z.string().nullable().optional(),
                details: z.unknown(),
                field: z.string().nullable().optional(),
                pointer: z.string().nullable().optional(),
                resource: z.string(),
            })
        )
        .nullable()
        .optional(),
    message: z.string(),
});

const Response201ZOD = z.object({
    data: z.object({
        bets: z.array(
            z.object({
                freebetCredits: z.array(z.any()).nullable().optional(),
                account: z.object({
                    id: z.number(),
                    name: z.string(),
                    type: z.union([
                        z.literal('customer'),
                        z.literal('staff'),
                        z.literal('program'),
                        z.literal('anonymous'),
                    ]),
                }),
                affiliate: z.string().nullable().optional(),
                cashOut: z.boolean(),
                channel: z.string(),
                comment: z.string().nullable().optional(),
                country: z.string().nullable().optional(),
                currency: z.string(),
                eachWay: z.boolean(),
                id: z.number().nullable().optional(),
                ip: z.string().nullable().optional(),
                legs: z.array(
                    z.union([
                        z.object({
                            eachWayOffered: z.boolean(),
                            eachWayTerms: z
                                .object({
                                    places: z.number(),
                                    reduction: z.string(),
                                })
                                .nullable()
                                .optional(),
                            event: z.object({
                                name: z.string(),
                            }),
                            eventCountry: z.array(z.string()),
                            id: z.string(),
                            inPlay: z.boolean(),
                            market: z
                                .object({
                                    type: z.string(),
                                })
                                .nullable()
                                .optional(),
                            price: z
                                .object({
                                    d: z.number(),
                                    f: z.string(),
                                })
                                .nullable()
                                .optional(),
                            priceType: z.union([z.literal('ep'), z.literal('bp'), z.literal('fp'), z.literal('sp')]),
                            result: z
                                .object({
                                    dividends: z
                                        .object({
                                            forecast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                            tricast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    '3rd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                        })
                                        .nullable()
                                        .optional(),
                                    place: z.number().nullable().optional(),
                                    placeReduction: z.string().nullable().optional(),
                                    r4Deductions: z.number().nullable().optional(),
                                    type: z.union([
                                        z.literal('won'),
                                        z.literal('lost'),
                                        z.literal('void'),
                                        z.literal('place'),
                                        z.literal('halfwon'),
                                        z.literal('halflost'),
                                    ]),
                                    winReduction: z.string().nullable().optional(),
                                })
                                .nullable()
                                .optional(),
                            selection: z.object({
                                name: z.string(),
                            }),
                            sport: z
                                .object({
                                    id: z.string(),
                                })
                                .nullable()
                                .optional(),
                            termsWithBet: z.boolean().nullable().optional(),
                            type: z.literal('empty'),
                            uuid: z.string().nullable().optional(),
                        }),
                        z.object({
                            competition: z
                                .object({
                                    id: z.string(),
                                    name: z.string(),
                                    url: z.string(),
                                })
                                .nullable()
                                .optional(),
                            eachWayOffered: z.boolean(),
                            eachWayTerms: z
                                .object({
                                    places: z.number(),
                                    reduction: z.string(),
                                })
                                .nullable()
                                .optional(),
                            event: z.object({
                                eventId: z.number().nullable().optional(),
                                name: z.string(),
                                platformEventId: z.string().nullable().optional(),
                                sport: z.string(),
                                status: z.union([
                                    z.literal('Prematch'),
                                    z.literal('Show'),
                                    z.literal('Started'),
                                    z.literal('Finished'),
                                ]),
                            }),
                            id: z.string(),
                            inPlay: z.boolean(),
                            parameters: z.unknown(),
                            price: z
                                .object({
                                    d: z.number(),
                                    f: z.string(),
                                })
                                .nullable()
                                .optional(),
                            priceType: z.union([z.literal('ep'), z.literal('bp'), z.literal('fp'), z.literal('sp')]),
                            result: z
                                .object({
                                    dividends: z
                                        .object({
                                            forecast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                            tricast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    '3rd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                        })
                                        .nullable()
                                        .optional(),
                                    place: z.number().nullable().optional(),
                                    placeReduction: z.string().nullable().optional(),
                                    r4Deductions: z.number().nullable().optional(),
                                    type: z.union([
                                        z.literal('won'),
                                        z.literal('lost'),
                                        z.literal('void'),
                                        z.literal('place'),
                                        z.literal('halfwon'),
                                        z.literal('halflost'),
                                    ]),
                                    winReduction: z.string().nullable().optional(),
                                })
                                .nullable()
                                .optional(),
                            selections: z.unknown(),
                            sport: z
                                .object({
                                    id: z.string(),
                                    name: z.string(),
                                    url: z.string(),
                                })
                                .nullable()
                                .optional(),
                            termsWithBet: z.boolean().nullable().optional(),
                            type: z.literal('rab'),
                            uuid: z.string().nullable().optional(),
                        }),
                        z.object({
                            competition: z.object({
                                id: z.string(),
                                name: z.string(),
                                url: z.string(),
                            }),
                            eachWayOffered: z.boolean(),
                            eachWayTerms: z
                                .object({
                                    places: z.number(),
                                    reduction: z.string(),
                                })
                                .nullable()
                                .optional(),
                            event: z.object({
                                id: z.string(),
                                idv2: z.string().nullable().optional(),
                                name: z.string(),
                                url: z.string(),
                            }),
                            eventCountry: z.array(z.string()),
                            id: z.string(),
                            inPlay: z.boolean(),
                            market: z.object({
                                id: z.number(),
                                idv2: z.string().nullable().optional(),
                                name: z.string(),
                                url: z.string(),
                            }),
                            price: z
                                .object({
                                    d: z.number(),
                                    f: z.string(),
                                })
                                .nullable()
                                .optional(),
                            priceType: z.union([z.literal('ep'), z.literal('bp'), z.literal('fp'), z.literal('sp')]),
                            result: z
                                .object({
                                    dividends: z
                                        .object({
                                            forecast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                            tricast: z.array(
                                                z.object({
                                                    '2nd': z.number(),
                                                    '3rd': z.number(),
                                                    dividend: z.number(),
                                                    id: z.number(),
                                                    winner: z.number(),
                                                })
                                            ),
                                        })
                                        .nullable()
                                        .optional(),
                                    place: z.number().nullable().optional(),
                                    placeReduction: z.string().nullable().optional(),
                                    r4Deductions: z.number().nullable().optional(),
                                    type: z.union([
                                        z.literal('won'),
                                        z.literal('lost'),
                                        z.literal('void'),
                                        z.literal('place'),
                                        z.literal('halfwon'),
                                        z.literal('halflost'),
                                    ]),
                                    winReduction: z.string().nullable().optional(),
                                })
                                .nullable()
                                .optional(),
                            selection: z.object({
                                id: z.number(),
                                idv2: z.string().nullable().optional(),
                                name: z.string(),
                            }),
                            spPrice: z
                                .object({
                                    d: z.number(),
                                    f: z.string(),
                                })
                                .nullable()
                                .optional(),
                            sport: z.object({
                                id: z.string(),
                                name: z.string(),
                                url: z.string(),
                            }),
                            termsWithBet: z.boolean().nullable().optional(),
                            type: z.literal('standard'),
                            uuid: z.string().nullable().optional(),
                        }),
                    ])
                ),
                maxBet: z.string().nullable().optional(),
                numLines: z.number(),
                operatorPayout: z.string().nullable().optional(),
                operatorProfit: z.string().nullable().optional(),
                operatorStakePerLine: z.string(),
                operatorTotalStake: z.string(),
                payout: z.string().nullable().optional(),
                placedAt: z.string(),
                placedBy: z
                    .object({
                        id: z.number(),
                        name: z.string(),
                        type: z.union([
                            z.literal('customer'),
                            z.literal('staff'),
                            z.literal('program'),
                            z.literal('anonymous'),
                        ]),
                    })
                    .nullable()
                    .optional(),
                potentialReturns: z.string().nullable().optional(),
                profit: z.string().nullable().optional(),
                remarks: z.array(
                    z.object({
                        code: z.string(),
                        debugDetails: z.string().nullable().optional(),
                        details: z.unknown(),
                        field: z.string().nullable().optional(),
                        pointer: z.string().nullable().optional(),
                        resource: z.string(),
                    })
                ),
                settledAt: z.string().nullable().optional(),
                settledBy: z
                    .object({
                        id: z.number(),
                        name: z.string(),
                        type: z.union([
                            z.literal('customer'),
                            z.literal('staff'),
                            z.literal('program'),
                            z.literal('anonymous'),
                        ]),
                    })
                    .nullable()
                    .optional(),
                stakeFactor: z.number(),
                stakePerLine: z.string(),
                status: z.union([
                    z.literal('open'),
                    z.literal('parked'),
                    z.literal('settled'),
                    z.literal('cancelled'),
                    z.literal('unsettled'),
                    z.literal('rejected'),
                    z.literal('initiated'),
                ]),
                totalStake: z.string(),
                transaction: z
                    .object({
                        assetFlows: z.array(
                            z.object({
                                balanceDelta: z.number(),
                                currency: z.string(),
                                date: z.string().nullable().optional(),
                                details: z.unknown(),
                                externalId: z.string().nullable().optional(),
                                id: z.number().nullable().optional(),
                                type: z.string(),
                            })
                        ),
                        preconditions: z.array(
                            z.object({
                                type: z.union([
                                    z.literal('withdrawableAtLeast'),
                                    z.literal('playableAtLeast'),
                                    z.literal('rejectFinalizedTransaction'),
                                ]),
                                value: z.number().nullable().optional(),
                            })
                        ),
                        tags: z
                            .object({
                                bonuses: z.array(
                                    z.object({
                                        amount: z.number(),
                                        id: z.number(),
                                        type: z.string(),
                                    })
                                ),
                                freebetCredits: z.array(
                                    z.object({
                                        amount: z.number(),
                                        id: z.number(),
                                    })
                                ),
                                rabMetadata: z
                                    .object({
                                        eventId: z.string(),
                                    })
                                    .nullable()
                                    .optional(),
                                selections: z.array(z.number()),
                            })
                            .nullable()
                            .optional(),
                        updatedAt: z.string(),
                        wallet: z.object({
                            account: z.object({
                                id: z.number(),
                                name: z.string(),
                                type: z.union([
                                    z.literal('customer'),
                                    z.literal('staff'),
                                    z.literal('program'),
                                    z.literal('anonymous'),
                                ]),
                            }),
                            id: z.string(),
                        }),
                    })
                    .nullable()
                    .optional(),
                type: z.union([
                    z.literal('SGL'),
                    z.literal('TTW'),
                    z.literal('TTP'),
                    z.literal('RAB'),
                    z.literal('DBL'),
                    z.literal('FC'),
                    z.literal('RFC'),
                    z.literal('SSA'),
                    z.literal('TTE'),
                    z.literal('TTRE'),
                    z.literal('TBL'),
                    z.literal('2F3'),
                    z.literal('TRX'),
                    z.literal('PAT'),
                    z.literal('RR'),
                    z.literal('TC'),
                    z.literal('CFC'),
                    z.literal('CTC'),
                    z.literal('TTT'),
                    z.literal('TTCE'),
                    z.literal('TTCT'),
                    z.literal('4F'),
                    z.literal('2F4'),
                    z.literal('3F4'),
                    z.literal('YAN'),
                    z.literal('L15'),
                    z.literal('FLG'),
                    z.literal('TTQ'),
                    z.literal('5F'),
                    z.literal('2F5'),
                    z.literal('3F5'),
                    z.literal('4F5'),
                    z.literal('L31'),
                    z.literal('CAN'),
                    z.literal('SFLG'),
                    z.literal('6F'),
                    z.literal('2F6'),
                    z.literal('3F6'),
                    z.literal('4F6'),
                    z.literal('5F6'),
                    z.literal('HNZ'),
                    z.literal('L63'),
                    z.literal('TTPP'),
                    z.literal('TTJP'),
                    z.literal('TTS6'),
                    z.literal('7F'),
                    z.literal('2F7'),
                    z.literal('3F7'),
                    z.literal('4F7'),
                    z.literal('5F7'),
                    z.literal('6F7'),
                    z.literal('SHNZ'),
                    z.literal('8F'),
                    z.literal('2F8'),
                    z.literal('3F8'),
                    z.literal('4F8'),
                    z.literal('5F8'),
                    z.literal('6F8'),
                    z.literal('7F8'),
                    z.literal('GOL'),
                    z.literal('9F'),
                    z.literal('2F9'),
                    z.literal('3F9'),
                    z.literal('8F9'),
                    z.literal('10F'),
                    z.literal('2F10'),
                    z.literal('3F10'),
                    z.literal('9F10'),
                    z.literal('11F'),
                    z.literal('2F11'),
                    z.literal('3F11'),
                    z.literal('10F11'),
                    z.literal('12F'),
                    z.literal('2F12'),
                    z.literal('3F12'),
                    z.literal('11F12'),
                    z.literal('13F'),
                    z.literal('2F13'),
                    z.literal('3F13'),
                    z.literal('12F13'),
                    z.literal('14F'),
                    z.literal('2F14'),
                    z.literal('3F14'),
                    z.literal('13F14'),
                    z.literal('15F'),
                    z.literal('2F15'),
                    z.literal('3F15'),
                    z.literal('14F15'),
                    z.literal('16F'),
                    z.literal('2F16'),
                    z.literal('3F16'),
                    z.literal('15F16'),
                    z.literal('17F'),
                    z.literal('2F17'),
                    z.literal('3F17'),
                    z.literal('16F17'),
                    z.literal('18F'),
                    z.literal('2F18'),
                    z.literal('3F18'),
                    z.literal('17F18'),
                    z.literal('19F'),
                    z.literal('2F19'),
                    z.literal('3F19'),
                    z.literal('18F19'),
                    z.literal('20F'),
                    z.literal('2F20'),
                    z.literal('3F20'),
                    z.literal('19F20'),
                    z.literal('21F'),
                    z.literal('2F21'),
                    z.literal('3F21'),
                    z.literal('20F21'),
                    z.literal('22F'),
                    z.literal('2F22'),
                    z.literal('3F22'),
                    z.literal('21F22'),
                    z.literal('23F'),
                    z.literal('2F23'),
                    z.literal('3F23'),
                    z.literal('22F23'),
                    z.literal('24F'),
                    z.literal('2F24'),
                    z.literal('3F24'),
                    z.literal('23F24'),
                    z.literal('25F'),
                    z.literal('2F25'),
                    z.literal('3F25'),
                    z.literal('24F25'),
                ]),
            })
        ),
    }),
});

const placeBetBetsSchema = Response201ZOD.shape.data.shape.bets;

export type PlaceBetBetsType = z.infer<typeof placeBetBetsSchema>;

const InternalPlaceBetErrors = ['request_fail', 'bet_params', 'possible_bet', 'retry-fail'] as const;
const InternalPlaceBetErrorsSchema = z.enum(InternalPlaceBetErrors);

type InternalPlaceBetErrorsType = (typeof InternalPlaceBetErrors)[number];
export const InternalPlaceBetErrorsMessages: Record<InternalPlaceBetErrorsType, string> = {
    request_fail: 'Request failed',
    bet_params: 'Wrong bet params',
    possible_bet: 'Possible bet failed',
    'retry-fail': 'Bet retry failed',
};

const ParsedPlaceBetSchema = z.union([
    z.object({
        status: z.literal('success'),
        data: placeBetBetsSchema,
    }),
    z.object({
        status: z.literal('error'),
        data: Response422ZOD,
    }),
    z.object({
        status: z.literal('internalError'),
        data: InternalPlaceBetErrorsSchema,
    }),
]);

export type ParsedPlaceBetType = z.infer<typeof ParsedPlaceBetSchema>;

export interface RabBetType {
    type: string;
    ip: string | undefined;
    stakePerLine: number | undefined;
    payout: number | undefined | null;
    eachWay: boolean;
    channel: ChannelType;
    platformId: string | null | undefined;
    correlationId: string;
    freeBets: Array<string> | null | undefined;
    freebetCredits: Array<{
        id: number;
        amount: string;
    }>;
    freebetRemarks: Array<{
        resource: string;
        code: string;
        details:
            | {
                  minimum: string;
              }
            | { invalid: string }
            | { negative: string };
    }>;
    legs: Array<{
        type: string | undefined;
        priceType: string;
        channel: ChannelType;
        sport: {
            id: string;
        };
        event: {
            id: number;
            externalId: string;
        };
        selections: RabPostBodyItemType[];
        price:
            | {
                  d: number;
                  f: string;
              }
            | null
            | undefined;
    }>;
}

export type PlaceBetRabBetType = Omit<RabBetType, 'stakePerLine'> & { stakePerLine: string };

export interface MultibetBetType {
    type: string;
    stakePerLine: number;
    eachWay: boolean;
    channel: ChannelType;
    country: string;
    ip: string;
    correlationId: string;
    sport?: string;
    freeBets?: Array<string> | null;
    freebetCredits?: Array<{
        id: number;
        newFreeBetId?: string;
        amount: string;
    }>;
    freebetRemarks?: Array<{
        resource: string;
        code: string;
        details:
            | {
                  minimum: string;
              }
            | { invalid: string }
            | { negative: string };
    }>;
    legs: Array<{
        type: string;
        legReference: {
            geniusMarketId: string;
            geniusFixtureId: string;
        };
        provider: string;
        internalEventId: number;
        price: {
            f: string;
            d: number;
        };
        priceType: string;
        name: string;
    }>;
}

export interface NewPlaceBetRequestType {
    channel: ChannelType;
    bets: Array<NewTradingPostBetData>;
    rabBets: Array<PlaceBetRabBetType> | null;
    multibetBets: Array<MultibetBetType> | null;
    possibleBetSuccess: boolean;
}

export const postPlaceBetNewTrading = async ({
    channel,
    bets,
    rabBets,
    multibetBets,
    possibleBetSuccess,
}: NewPlaceBetRequestType): Promise<BettingPlaceBetType> => {
    const response = await fetchPost({
        url: '/api/betslip/place-bet-from-website-bets',
        decode: decodePostPlaceBetResponse,
        body: { channel, bets, rabBets, multibetBets, possibleBetSuccess },
    });
    if (response.status === 200) {
        return {
            status: 'success',
            data: response.bodyJson,
        };
    }

    if (response.status === 400 || response.status === 403) {
        const { data, debug, errors: responseErrors } = response.bodyJson;
        // TODO: To be properly handled after betslip refactor
        const errors = Array.isArray(responseErrors) ? responseErrors : {};
        return {
            status: 'error',
            data: data,
            debug: debug,
            errors,
        };
    }

    // Handle any other error status
    return {
        status: 'error',
        data: null,
        debug: `Unexpected response status: ${response.status}`,
        errors: {},
    };
};
